import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  createDoctorRound,
  getDoctorRoundById,
  listDoctorRoundsByAdmission,
  searchServices,
  updateDoctorRound,
} from "../../services/doctorRoundService";

const MOCK_DOCTORS = [
  { doctorId: 1, doctorName: "Dr Mahesh Mandora" },
  { doctorId: 2, doctorName: "Dr Anita Rao" },
  { doctorId: 3, doctorName: "Dr Prasad Deshmukh" },
];

const initialState = {
  patient: null,
  doctors: MOCK_DOCTORS,
  services: [],
  servicesLoading: false,
  advisedServices: [],
  roundHistory: [],
  editingDoctorNoteId: null,
  formData: {
    date: new Date().toISOString().split("T")[0],
    time: "",
    doctor: null,
    addedBy: "",
    tpr: false,
    tprTimes: "",
    intakeFlag: false,
    intakeTimes: "",
    doctorNotes: "",
  },
  loading: false,
  saving: false,
  error: null,
};

export const searchDoctorRoundServices = createAsyncThunk(
  "drRound/searchDoctorRoundServices",
  async (query, { rejectWithValue }) => {
    try {
      return await searchServices(query);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDoctorRounds = createAsyncThunk(
  "drRound/fetchDoctorRounds",
  async (admissionId, { rejectWithValue }) => {
    try {
      return await listDoctorRoundsByAdmission(admissionId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchDoctorRoundById = createAsyncThunk(
  "drRound/fetchDoctorRoundById",
  async (doctorNoteId, { rejectWithValue }) => {
    try {
      return await getDoctorRoundById(doctorNoteId);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveDoctorRoundNote = createAsyncThunk(
  "drRound/saveDoctorRoundNote",
  async ({ doctorNoteId, payload }, { rejectWithValue }) => {
    try {
      if (doctorNoteId) {
        return await updateDoctorRound(doctorNoteId, payload);
      }
      return await createDoctorRound(payload);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const drRoundSlice = createSlice({
  name: "drRound",
  initialState,
  reducers: {
    setFormField: (state, action) => {
      const { field, value } = action.payload;
      state.formData[field] = value;
    },
    resetForm: (state) => {
      const now = new Date();
      state.formData = {
        date: now.toISOString().split("T")[0],
        time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
        doctor: null,
        addedBy: "",
        tpr: false,
        tprTimes: "",
        intakeFlag: false,
        intakeTimes: "",
        doctorNotes: "",
      };
      state.advisedServices = [];
      state.editingDoctorNoteId = null;
    },
    addAdvisedService: (state, action) => {
      const serviceId = Number(action.payload.serviceId);
      const exists = state.advisedServices.some(
        (s) => Number(s.serviceId) === serviceId
      );
      if (!exists) {
        state.advisedServices.push({
          ...action.payload,
          serviceId,
        });
      }
    },
    removeAdvisedService: (state, action) => {
      state.advisedServices = state.advisedServices.filter(
        (s) => s.id !== action.payload
      );
    },
    setAdvisedServices: (state, action) => {
      state.advisedServices = action.payload || [];
    },
    /** Merge a live API doctor into the dropdown list when missing from mocks. */
    upsertDoctor: (state, action) => {
      const doctorId = Number(action.payload?.doctorId);
      const doctorName = (action.payload?.doctorName || "").trim();
      if (!Number.isFinite(doctorId) || doctorId <= 0 || !doctorName) return;
      const exists = state.doctors.some((d) => Number(d.doctorId) === doctorId);
      if (!exists) {
        state.doctors.push({ doctorId, doctorName });
      }
    },
    setPatient: (state, action) => {
      state.patient = action.payload;
      state.roundHistory = [];
      state.editingDoctorNoteId = null;
    },
    setEditingDoctorNoteId: (state, action) => {
      state.editingDoctorNoteId = action.payload;
    },
    setRoundHistory: (state, action) => {
      state.roundHistory = action.payload || [];
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDoctorRoundServices.pending, (state) => {
        state.servicesLoading = true;
      })
      .addCase(searchDoctorRoundServices.fulfilled, (state, action) => {
        state.servicesLoading = false;
        state.services = (action.payload || []).map((s) => ({
          serviceId: Number(s.serviceId),
          serviceCode: s.serviceCode || "",
          serviceName: s.serviceName || "",
          price: s.price,
        }));
      })
      .addCase(searchDoctorRoundServices.rejected, (state, action) => {
        state.servicesLoading = false;
        state.services = [];
        state.error = action.payload || "Failed to search services";
      })
      .addCase(fetchDoctorRounds.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorRounds.fulfilled, (state, action) => {
        state.loading = false;
        state.roundHistory = action.payload || [];
      })
      .addCase(fetchDoctorRounds.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load doctor rounds";
        state.roundHistory = [];
      })
      .addCase(fetchDoctorRoundById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctorRoundById.fulfilled, (state, action) => {
        state.loading = false;
        const detail = action.payload;
        const note = detail?.note;
        if (note?.doctorNoteId) {
          state.editingDoctorNoteId = note.doctorNoteId;
        }
        if (note?.doctorId && note?.doctorName) {
          const doctorId = Number(note.doctorId);
          if (
            Number.isFinite(doctorId) &&
            doctorId > 0 &&
            !state.doctors.some((d) => Number(d.doctorId) === doctorId)
          ) {
            state.doctors.push({
              doctorId,
              doctorName: String(note.doctorName).trim(),
            });
          }
        }
        const services = detail?.services || [];
        state.advisedServices = services.map((s) => ({
          id: `${s.id || s.serviceId}`,
          serviceId: Number(s.serviceId),
          serviceCode: s.serviceCode || s.serviceId,
          serviceName: s.serviceName || "",
        }));
      })
      .addCase(fetchDoctorRoundById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load doctor round note";
      })
      .addCase(saveDoctorRoundNote.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveDoctorRoundNote.fulfilled, (state) => {
        state.saving = false;
        state.editingDoctorNoteId = null;
      })
      .addCase(saveDoctorRoundNote.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || "Failed to save doctor round";
      });
  },
});

export const {
  setFormField,
  resetForm,
  addAdvisedService,
  removeAdvisedService,
  setAdvisedServices,
  upsertDoctor,
  setPatient,
  setEditingDoctorNoteId,
  setRoundHistory,
  setLoading,
  clearError,
} = drRoundSlice.actions;

export default drRoundSlice.reducer;
